import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenantLocation, useMoney } from '../context/LocationContext';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Trophy, TrendingUp, TrendingDown, Minus, AlertTriangle, RefreshCw,
  Loader2, MapPin, ArrowRight, Activity, Banknote, Receipt, AlertCircle,
  CalendarRange, FileDown, Printer,
} from 'lucide-react';

const RANGE_OPTIONS = [
  { value: 'today',      label: 'Today' },
  { value: 'last_7d',    label: 'Last 7 days' },
  { value: 'last_30d',   label: 'Last 30 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'custom',     label: 'Custom range…' },
];

/**
 * Side-by-side comparison of every active location. Sorted by revenue in
 * the selected window, with a visible "Trailing" flag on the weakest venue.
 */
export default function CompareLocations() {
  const { token } = useAuth();
  const { setCurrentLocationId } = useTenantLocation();
  const fm = useMoney();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState(null);

  const [range, setRange] = useState('last_7d');
  const today = new Date().toISOString().slice(0, 10);
  const [customStart, setCustomStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [customEnd, setCustomEnd] = useState(today);

  const load = useCallback(async () => {
    try {
      const params = { range };
      if (range === 'custom') {
        params.start = customStart;
        params.end = customEnd;
      }
      const r = await api.compareLocations(token, params);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load comparison');
    } finally { setLoading(false); setRefreshing(false); }
  }, [token, range, customStart, customEnd]);

  useEffect(() => { load(); }, [load]);

  const jumpTo = (loc) => {
    setCurrentLocationId(loc.location_id);
    navigate('/dashboard');
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = { range };
      if (range === 'custom') { params.start = customStart; params.end = customEnd; }
      // Fetch as blob so we can force a browser download with the right filename
      const url = api.compareLocationsExportUrl(token, params);
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('CSV download failed');
      const blob = await r.blob();
      const dispo = r.headers.get('Content-Disposition') || '';
      const nameMatch = dispo.match(/filename="([^"]+)"/);
      const filename = nameMatch ? nameMatch[1] : `syncpoint-comparison.csv`;
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
      toast.success('CSV exported');
    } catch (e) {
      toast.error(e.message || 'CSV export failed');
    } finally { setExporting(false); }
  };

  const printReport = () => window.print();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  const locations = data?.locations || [];
  const totals = data?.totals || {};
  const period = data?.period || {};
  const maxRev = Math.max(1, ...locations.map((l) => l.revenue_window));
  const leader = locations[0];
  const trailing = locations.find((l) => l.is_trailing);

  // Duration for the "vs previous" copy under the range picker
  let periodDays = 0;
  if (period.start && period.end) {
    periodDays = Math.round((new Date(period.end).getTime() - new Date(period.start).getTime()) / 86400000);
  }

  return (
    <div className="space-y-6 animate-fade-in print:space-y-3" data-testid="compare-locations-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-lg bg-blue-500/15 border border-blue-500/30 flex items-center justify-center print:hidden">
            <Trophy className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-white tracking-tight">Compare locations</h1>
            <p className="text-slate-400 mt-1 max-w-2xl">
              Revenue and sync health side-by-side across every venue for <span className="text-blue-300 font-medium">{period.label}</span>. The trailing location is flagged so you know where to focus.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 print:hidden">
          <Button variant="outline" onClick={() => { setRefreshing(true); load(); }}
            disabled={refreshing} className="border-slate-700 text-slate-200 hover:text-white" data-testid="compare-refresh-btn">
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Refresh
          </Button>
          <Button variant="outline" onClick={exportCsv} disabled={exporting || locations.length === 0}
            className="border-slate-700 text-slate-200 hover:text-white" data-testid="compare-export-csv-btn">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileDown className="w-4 h-4 mr-2" />}
            Export CSV
          </Button>
          <Button variant="outline" onClick={printReport} disabled={locations.length === 0}
            className="border-slate-700 text-slate-200 hover:text-white" data-testid="compare-print-btn">
            <Printer className="w-4 h-4 mr-2" />
            Print / PDF
          </Button>
        </div>
      </div>

      {/* Range picker */}
      <Card className="bg-slate-900/50 border-slate-800 print:hidden">
        <CardContent className="p-4 flex flex-wrap gap-3 items-end">
          <div className="min-w-[220px]">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1 flex items-center gap-1"><CalendarRange className="w-3 h-3" /> Period</div>
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger className="bg-slate-950 border-slate-800 text-white" data-testid="range-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 text-white">
                {RANGE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} className="focus:bg-slate-800 focus:text-white">
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {range === 'custom' && (
            <>
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Start</div>
                <Input type="date" value={customStart} max={customEnd}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="bg-slate-950 border-slate-800 text-white" data-testid="range-start" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">End (exclusive)</div>
                <Input type="date" value={customEnd} min={customStart} max={today}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="bg-slate-950 border-slate-800 text-white" data-testid="range-end" />
              </div>
            </>
          )}
          <div className="ml-auto text-xs text-slate-500 pb-2">
            {periodDays > 0 ? `Comparing against the previous ${periodDays} day${periodDays === 1 ? '' : 's'}` : ''}
          </div>
        </CardContent>
      </Card>

      {locations.length === 0 && (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-12 text-center text-slate-400 space-y-3">
            <MapPin className="w-10 h-10 opacity-40 mx-auto" />
            <div className="text-white font-medium">No locations yet</div>
            <p className="text-sm">Add at least two locations to start comparing.</p>
            <Button onClick={() => navigate('/locations')} className="bg-blue-500 hover:bg-blue-600 text-white mt-2">
              Add locations <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}

      {locations.length === 1 && (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-6 text-center text-slate-400">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
            Only one location on this tenant. Add a second location to see side-by-side comparisons.
            <div className="mt-3">
              <Button onClick={() => navigate('/locations')} variant="outline" className="border-slate-700 text-slate-200 hover:text-white">
                Manage locations <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {locations.length >= 2 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <TotalCard label={`Revenue · ${period.label}`} value={fm.totals(locations, (l) => l.revenue_window)} icon={<TrendingUp className="w-4 h-4" />} accent="blue" />
            <TotalCard label="Revenue · Today" value={fm.totals(locations, (l) => l.revenue_today)} icon={<Banknote className="w-4 h-4" />} accent="emerald" />
            <TotalCard label={`Sales · ${period.label}`} value={String(totals.sales_window ?? 0)} icon={<Receipt className="w-4 h-4" />} accent="purple" />
            <TotalCard label="Sync failures in period" value={String(totals.sync_failed_window ?? 0)} icon={<AlertTriangle className="w-4 h-4" />} accent={totals.sync_failed_window ? 'rose' : 'slate'} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {leader && (
              <CalloutCard
                tone="emerald"
                title={`Leading · ${leader.location_name}`}
                subtitle={`${fm.money(leader.revenue_window, leader.location_id)} in ${period.label.toLowerCase()}`}
                icon={<Trophy className="w-5 h-5" />}
                testId="callout-leader"
              />
            )}
            {trailing && (
              <CalloutCard
                tone="amber"
                title={`Trailing · ${trailing.location_name}`}
                subtitle={
                  trailing.revenue_window === 0
                    ? `No completed sales during ${period.label.toLowerCase()}`
                    : `${fm.money(trailing.revenue_window, trailing.location_id)} — ${diffPct(leader.revenue_window, trailing.revenue_window)} behind the leader`
                }
                icon={<AlertTriangle className="w-5 h-5" />}
                testId="callout-trailing"
              />
            )}
          </div>

          <Card className="bg-slate-900/50 border-slate-800 print:border-slate-300">
            <CardHeader>
              <CardTitle className="text-white font-heading text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-slate-300" /> Location scoreboard · {period.label}
              </CardTitle>
              <CardDescription className="text-slate-500">
                Ranked by revenue in the selected window. Click any row to switch the whole dashboard to that venue.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto" data-testid="compare-scoreboard-scroll">
              <div className="min-w-[900px]">
              <div className="grid grid-cols-12 px-4 py-2 bg-slate-900/70 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
                <div className="col-span-3">Location</div>
                <div className="col-span-3">Revenue in window</div>
                <div className="col-span-1 text-right">Today</div>
                <div className="col-span-1 text-right">Sales</div>
                <div className="col-span-2 pl-4">Sync health</div>
                <div className="col-span-1 text-right">Alerts</div>
                <div className="col-span-1 text-right">Rank</div>
              </div>
              <div className="divide-y divide-slate-800/70">
                {locations.map((loc) => (
                  <LocationRow key={loc.location_id} loc={loc} maxRev={maxRev} onJump={() => jumpTo(loc)} />
                ))}
              </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function LocationRow({ loc, maxRev, onJump }) {
  const { money } = useMoney();
  const pct = Math.max(2, Math.round((loc.revenue_window / maxRev) * 100));
  const barColor = loc.is_trailing ? 'bg-amber-500/70' : loc.rank === 1 ? 'bg-emerald-500/70' : 'bg-blue-500/70';
  return (
    <button
      type="button"
      onClick={onJump}
      className="w-full grid grid-cols-12 px-4 py-3 items-center hover:bg-slate-800/30 transition-colors text-left"
      data-testid={`compare-row-${loc.location_id}`}
    >
      <div className="col-span-3 min-w-0">
        <div className="flex items-center gap-2 text-white truncate">
          <MapPin className="w-3.5 h-3.5 text-blue-300 flex-shrink-0 print:hidden" />
          <span className="truncate font-medium">{loc.location_name}</span>
          {loc.is_default && (
            <Badge className="bg-amber-500/10 text-amber-300 border-amber-500/30 text-[9px] px-1 py-0">DEFAULT</Badge>
          )}
        </div>
        <div className="text-[10px] text-slate-500 mt-0.5 truncate">
          {loc.verified ? 'verified' : 'not verified'} · last sync {formatRelative(loc.last_sync_at)}
        </div>
      </div>

      <div className="col-span-3 pr-4">
        <div className="flex items-baseline gap-2">
          <span className="text-white font-mono font-medium">{money(loc.revenue_window, loc.location_id)}</span>
          <TrendPill pct={loc.revenue_trend_pct} />
        </div>
        <div className="mt-1.5 h-1.5 rounded-full bg-slate-800 overflow-hidden">
          <div className={`h-full ${barColor}`} style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="col-span-1 text-right text-slate-200 font-mono text-sm">
        {money(loc.revenue_today, loc.location_id)}
      </div>

      <div className="col-span-1 text-right text-slate-300 font-mono text-sm">
        {loc.sales_window}
      </div>

      <div className="col-span-2 flex items-center gap-2 pl-4">
        <SyncHealthBadge loc={loc} />
      </div>

      <div className="col-span-1 text-right">
        {loc.inventory_alerts > 0 ? (
          <Badge className="bg-rose-500/15 text-rose-300 border-rose-500/30 font-mono">{loc.inventory_alerts}</Badge>
        ) : (
          <span className="text-slate-600 text-sm">—</span>
        )}
      </div>

      <div className="col-span-1 text-right">
        <RankBadge loc={loc} />
      </div>
    </button>
  );
}

function TotalCard({ label, value, icon, accent }) {
  const border = {
    emerald: 'border-emerald-500/30',
    blue: 'border-blue-500/30',
    purple: 'border-purple-500/30',
    rose: 'border-rose-500/30',
    slate: 'border-slate-700',
  }[accent] || 'border-slate-800';
  const color = {
    emerald: 'text-emerald-300',
    blue: 'text-blue-300',
    purple: 'text-purple-300',
    rose: 'text-rose-300',
    slate: 'text-slate-400',
  }[accent] || 'text-slate-200';
  return (
    <div className={`rounded-lg bg-slate-900/60 border ${border} p-4`}>
      <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wide ${color} font-medium`}>{icon}{label}</div>
      <div className="text-2xl font-mono font-bold text-white mt-1">{value}</div>
    </div>
  );
}

function CalloutCard({ tone, title, subtitle, icon, testId }) {
  const c = tone === 'emerald'
    ? 'from-emerald-500/[0.12] to-slate-900/40 border-emerald-500/30 text-emerald-200'
    : 'from-amber-500/[0.12] to-slate-900/40 border-amber-500/30 text-amber-200';
  return (
    <div className={`rounded-xl bg-gradient-to-r ${c} border p-4 flex items-start gap-3`} data-testid={testId}>
      <div className="w-9 h-9 rounded-lg bg-slate-950/40 border border-white/10 flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div>
        <div className="text-white font-medium text-sm">{title}</div>
        <div className="text-xs text-slate-300 mt-0.5">{subtitle}</div>
      </div>
    </div>
  );
}

function TrendPill({ pct }) {
  if (pct === null || pct === undefined) {
    return <span className="text-[10px] text-slate-500 font-mono">new</span>;
  }
  if (pct === 0) {
    return <span className="text-[10px] text-slate-500 font-mono inline-flex items-center gap-0.5"><Minus className="w-2.5 h-2.5" />0%</span>;
  }
  const up = pct > 0;
  const cls = up ? 'text-emerald-300' : 'text-rose-300';
  return (
    <span className={`text-[10px] ${cls} font-mono inline-flex items-center gap-0.5`}>
      {up ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
      {pct > 0 ? '+' : ''}{pct}%
    </span>
  );
}

function SyncHealthBadge({ loc }) {
  if (loc.sync_health_pct === null || loc.sync_health_pct === undefined) {
    return <span className="text-xs text-slate-500">idle · no syncs</span>;
  }
  const good = loc.sync_status === 'healthy';
  const bad = loc.sync_status === 'failing';
  const cls = bad
    ? 'bg-rose-500/15 text-rose-300 border-rose-500/30'
    : good
      ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
      : 'bg-amber-500/15 text-amber-300 border-amber-500/30';
  return (
    <>
      <Badge className={`${cls} font-mono`}>{loc.sync_health_pct}%</Badge>
      {loc.sync_failed_window > 0 && (
        <span className="text-[10px] text-rose-300">· {loc.sync_failed_window} failed</span>
      )}
    </>
  );
}

function RankBadge({ loc }) {
  if (loc.is_trailing) {
    return <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 font-mono">trailing</Badge>;
  }
  if (loc.rank === 1) {
    return <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 font-mono">#1</Badge>;
  }
  return <Badge className="bg-slate-800 text-slate-400 border-slate-700 font-mono">#{loc.rank}</Badge>;
}

function diffPct(a, b) {
  if (!a) return '—';
  const d = ((a - b) / a) * 100;
  return `${d.toFixed(0)}%`;
}

function formatRelative(iso) {
  if (!iso) return 'never';
  const dt = new Date(iso);
  const secs = (Date.now() - dt.getTime()) / 1000;
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}
